import React from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  VStack,
  HStack,
  Text,
  Badge,
  IconButton,
  Divider,
  Tooltip,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react'
import { FiEdit3, FiTrash2, FiUsers, FiFile, FiTag, FiMessageSquare } from 'react-icons/fi'
import { Plan } from '@/hooks/usePlans'

interface PlanCardProps {
  plan: Plan
  onEdit: (plan: Plan) => void
  onDelete: (plan: Plan) => void
}

export default function PlanCard({ plan, onEdit, onDelete }: PlanCardProps) {
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400')

  const getDisplayPrice = (): string => {
    if (!plan.price_monthly) {
      return 'Free'
    }
    return `$${plan.price_monthly}/month`
  }

  const getPlanTypeColor = (): string => {
    return plan.type === 'preset' ? 'blue' : 'orange'
  }

  const isDeleteDisabled = plan.organizations_count > 0

  return (
    <Card
      bg={cardBg}
      borderRadius="xl"
      border="1px solid"
      borderColor={borderColor}
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: 'xl',
        borderColor: 'blue.300',
      }}
      transition="all 0.3s"
      height="full"
    >
      <CardHeader pb={3}>
        <Flex align="start" justify="space-between">
          <VStack align="start" spacing={2} flex={1}>
            <HStack>
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                {plan.name}
              </Text>
              <Badge colorScheme={getPlanTypeColor()} variant="subtle">
                {plan.type}
              </Badge>
              {!plan.is_active && (
                <Badge colorScheme="gray" variant="subtle">
                  Inactive
                </Badge>
              )}
            </HStack>
            <Text fontSize="2xl" fontWeight="bold" color="green.600">
              {getDisplayPrice()}
            </Text>
          </VStack>
        </Flex>
      </CardHeader>

      <CardBody pt={0}>
        <VStack spacing={4} align="stretch">
          {/* Description */}
          {plan.description && (
            <Text fontSize="sm" color={mutedTextColor} noOfLines={2}>
              {plan.description}
            </Text>
          )}

          <Divider />

          {/* Key Limits */}
          <VStack spacing={3} align="stretch">
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              Plan Limits
            </Text>
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <FiFile size={14} color="currentColor" />
                  <Text fontSize="sm" color={mutedTextColor}>
                    Pages
                  </Text>
                </HStack>
                <Text fontSize="sm" fontWeight="medium">
                  {plan.limits.page_limit.toLocaleString()}
                </Text>
              </HStack>

              <HStack justify="space-between">
                <HStack spacing={2}>
                  <FiFile size={14} color="currentColor" />
                  <Text fontSize="sm" color={mutedTextColor}>
                    Files
                  </Text>
                </HStack>
                <Text fontSize="sm" fontWeight="medium">
                  {plan.limits.file_limit.toLocaleString()}
                </Text>
              </HStack>

              <HStack justify="space-between">
                <HStack spacing={2}>
                  <FiTag size={14} color="currentColor" />
                  <Text fontSize="sm" color={mutedTextColor}>
                    Smart Tags
                  </Text>
                </HStack>
                <Text fontSize="sm" fontWeight="medium">
                  {plan.limits.smart_tagging_limit.toLocaleString()}/mo
                </Text>
              </HStack>

              <HStack justify="space-between">
                <HStack spacing={2}>
                  <FiMessageSquare size={14} color="currentColor" />
                  <Text fontSize="sm" color={mutedTextColor}>
                    Chat Queries
                  </Text>
                </HStack>
                <Text fontSize="sm" fontWeight="medium">
                  {plan.limits.chatbot_queries_limit.toLocaleString()}/mo
                </Text>
              </HStack>

              <HStack justify="space-between">
                <HStack spacing={2}>
                  <FiUsers size={14} color="currentColor" />
                  <Text fontSize="sm" color={mutedTextColor}>
                    Users
                  </Text>
                </HStack>
                <Text fontSize="sm" fontWeight="medium">
                  {plan.limits.users_limit}
                </Text>
              </HStack>
            </VStack>
          </VStack>

          <Divider />

          {/* Usage Stats */}
          <HStack justify="space-between" align="center">
            <VStack spacing={0} align="start">
              <Text fontSize="xs" color={mutedTextColor}>
                Organizations Using
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="blue.600">
                {plan.organizations_count}
              </Text>
            </VStack>

            <Text fontSize="xs" color={mutedTextColor}>
              Created {new Date(plan.created_at).toLocaleDateString()}
            </Text>
          </HStack>

          <Divider />

          {/* Actions */}
          <HStack spacing={3} justify="center">
            <Tooltip label="Edit plan" placement="top">
              <IconButton
                aria-label="Edit plan"
                icon={<FiEdit3 />}
                size="sm"
                variant="ghost"
                onClick={() => onEdit(plan)}
                _hover={{ bg: 'blue.50', color: 'blue.600' }}
              />
            </Tooltip>

            <Tooltip
              label={
                isDeleteDisabled ? 'Cannot delete plan with assigned organizations' : 'Delete plan'
              }
              placement="top"
            >
              <IconButton
                aria-label="Delete plan"
                icon={<FiTrash2 />}
                size="sm"
                variant="ghost"
                onClick={() => onDelete(plan)}
                isDisabled={isDeleteDisabled}
                _hover={{ bg: 'red.50', color: 'red.600' }}
                color={isDeleteDisabled ? 'gray.400' : 'gray.600'}
              />
            </Tooltip>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  )
}
